import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector, batch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { updateSegment, setDepartureTime } from '../../../redux/currentSearch/actions';
import {
    getDateErrorSelector,
    getSegments,
    displayFaresInCalendar,
    getDateRangeConfig,
} from '../../../redux/currentSearch/selectors';
import { CurrentAccommodationSearchSessionStorage, DateUtils } from '../../../utils';
import { useSegmentContext } from './SegmentManager/Context';
import { useSearchId } from '../../SearchManager/SearchIdProvider';
import { useOneWayDatePreselection } from '../../../utils/hooks/useOneWayDatePreselection';

export const SegmentDate = ({
    children,
    segments,
    updateSegment,
    setDepartureTime,
    dateError,
    displayFaresInCalendar,
    maxDays,
    minDays,
}) => {
    const { updateSearchId } = useSearchId();
    const { segment } = useSegmentContext();
    const { id, date } = segment;

    const rangeConfigQuery = useMemo(() => ({ id }), [id]);
    const { min: minOffset, max: maxOffset } = useSelector((state) => getDateRangeConfig(state, rangeConfigQuery));
    const dates = useMemo(() => {
        return segments.map((s) => DateUtils.stringToDate(s.date));
    }, [segments]);

    const [minDate, setMinDate] = React.useState(DateUtils.calculateMinDateFromSegments(segments, id, minDays));
    const [maxDate, setMaxDate] = React.useState(DateUtils.calculateMaxDateFromSegments(segments, id, maxDays));

    useOneWayDatePreselection(id, date);

    const setValue = useCallback((newDate) => {
        batch(() => {
            updateSegment({ date: newDate }, id);
            setDepartureTime(id, null);
        });

        CurrentAccommodationSearchSessionStorage.setDates({ id, date: newDate, segments });
        updateSearchId();
    }, [id, segments, setDepartureTime, updateSearchId, updateSegment]);

    useEffect(() => {
        const calculatedMinDate = DateUtils.calculateMinDateFromSegments(segments, id, minDays);
        const calculatedMaxDate = DateUtils.calculateMaxDateFromSegments(segments, id, maxDays);
        const minDateWithOffset = DateUtils.add(calculatedMinDate, minOffset);

        setMinDate(minDateWithOffset);
        setMaxDate(maxOffset ? DateUtils.add(calculatedMinDate, maxOffset) : calculatedMaxDate);
    }, [id, maxDays, maxOffset, minDays, minOffset, segments]);

    useEffect(function clearDateIfBeforeMinDate() {
        if (
            date &&
            DateUtils.compare(DateUtils.stringToDate(date), DateUtils.stringToDate(minDate)) === 1
        ) {
            updateSegment({ date: null }, id);
        }
    }, [date, id, minDate, updateSegment]);

    useEffect(function clearDateIfAfterMaxDate() {
        if (
            date &&
            DateUtils.compare(DateUtils.stringToDate(maxDate), DateUtils.stringToDate(date)) === 1
        ) {
            updateSegment({ date: null }, id);
        }
    }, [date, id, maxDate, updateSegment]);

    return children({
        error: dateError,
        dates,
        minDate,
        maxDate,
        setValue,
        value: date,
        displayFaresInCalendar,
    });
};

SegmentDate.propTypes = {
    children: PropTypes.func.isRequired,
    updateSegment: PropTypes.func.isRequired,
    displayFaresInCalendar: PropTypes.bool.isRequired,
    maxDays: PropTypes.number,
    minDays: PropTypes.number,
};
SegmentDate.defaultProps = {
    maxDays: -1,
    minDays: 0
};

const mapStateToPropsSelector = createStructuredSelector({
    segments: getSegments,
    dateError: getDateErrorSelector,
    displayFaresInCalendar,
});

export const ConnectedSegmentDate = connect(mapStateToPropsSelector, {
    updateSegment,
    setDepartureTime,
})(SegmentDate);
